// Generate a short unique ID
const uid = () => Math.random().toString(36).slice(2, 10);

export const SECTION_TYPES = {
  SUMMARY: 'summary',
  EXPERIENCE: 'experience',
  EDUCATION: 'education',
  SKILLS: 'skills',
  CERTIFICATIONS: 'certifications',
  PROJECTS: 'projects',
};

export const SECTION_LABELS = {
  summary: 'Professional Summary',
  experience: 'Experience',
  education: 'Education',
  skills: 'Skills',
  certifications: 'Certifications',
  projects: 'Projects',
};

export const createEmptySection = (type) => {
  const base = { id: uid(), type, title: SECTION_LABELS[type] || type };
  switch (type) {
    case 'summary':
      return { ...base, content: '' };
    case 'experience':
      return { ...base, items: [createExperienceItem()] };
    case 'education':
      return { ...base, items: [createEducationItem()] };
    case 'skills':
      return { ...base, items: [''] };
    case 'certifications':
      return { ...base, items: [''] };
    case 'projects':
      return { ...base, items: [createProjectItem()] };
    default:
      return { ...base, content: '' };
  }
};

export const createExperienceItem = () => ({
  id: uid(),
  title: '',
  company: '',
  location: '',
  startDate: '',
  endDate: '',
  current: false,
  bullets: [''],
});

export const createEducationItem = () => ({
  id: uid(),
  degree: '',
  school: '',
  year: '',
  details: '',
});

export const createProjectItem = () => ({
  id: uid(),
  name: '',
  description: '',
  tech: '',
  url: '',
});

export const DEFAULT_RESUME = {
  name: '',
  email: '',
  phone: '',
  location: '',
  linkedin: '',
  website: '',
  sections: [
    createEmptySection('summary'),
    createEmptySection('experience'),
    createEmptySection('education'),
    createEmptySection('skills'),
  ],
};

export const TEMPLATE_STYLES = {
  classic: {
    id: 'classic',
    name: 'Classic',
    description: 'Traditional professional layout',
    headerBg: '#ffffff',
    headerText: '#0f1115',
    accentColor: '#002fa7',
    bodyFont: "'IBM Plex Sans', sans-serif",
    headerFont: "'IBM Plex Sans', sans-serif",
  },
  modern: {
    id: 'modern',
    name: 'Modern',
    description: 'Clean with bold accent bar',
    headerBg: '#0f1115',
    headerText: '#ffffff',
    accentColor: '#002fa7',
    bodyFont: "'IBM Plex Sans', sans-serif",
    headerFont: "'IBM Plex Sans', sans-serif",
  },
  minimal: {
    id: 'minimal',
    name: 'Minimal',
    description: 'Light and spacious',
    headerBg: '#f8fafc',
    headerText: '#0f1115',
    accentColor: '#64748b',
    bodyFont: "'IBM Plex Sans', sans-serif",
    headerFont: "'IBM Plex Sans', sans-serif",
  },
  executive: {
    id: 'executive',
    name: 'Executive',
    description: 'Refined for senior roles',
    headerBg: '#1e293b',
    headerText: '#f8fafc',
    accentColor: '#c9a227',
    bodyFont: "'IBM Plex Sans', sans-serif",
    headerFont: "'IBM Plex Sans', sans-serif",
  },
  creative: {
    id: 'creative',
    name: 'Creative',
    description: 'Bold colors for design roles',
    headerBg: '#7c3aed',
    headerText: '#ffffff',
    accentColor: '#7c3aed',
    bodyFont: "'IBM Plex Sans', sans-serif",
    headerFont: "'IBM Plex Sans', sans-serif",
  },
};

// Convert structured resume data to plain text for ATS scoring
export const resumeToPlainText = (resume) => {
  let text = '';
  if (resume.name) text += `${resume.name}\n`;
  const contactParts = [resume.email, resume.phone, resume.location, resume.linkedin, resume.website].filter(Boolean);
  if (contactParts.length) text += contactParts.join(' | ') + '\n\n';

  for (const section of resume.sections) {
    text += `${section.title.toUpperCase()}\n`;
    switch (section.type) {
      case 'summary':
        text += (section.content || '') + '\n\n';
        break;
      case 'experience':
        for (const item of section.items || []) {
          const dates = item.current ? `${item.startDate} - Present` : `${item.startDate} - ${item.endDate}`;
          text += `${item.title} | ${item.company}${item.location ? ` | ${item.location}` : ''} | ${dates}\n`;
          for (const b of item.bullets || []) {
            if (b.trim()) text += `- ${b}\n`;
          }
          text += '\n';
        }
        break;
      case 'education':
        for (const item of section.items || []) {
          text += `${item.degree} | ${item.school}${item.year ? ` | ${item.year}` : ''}\n`;
          if (item.details) text += `${item.details}\n`;
          text += '\n';
        }
        break;
      case 'skills':
        text += (section.items || []).filter(Boolean).join(', ') + '\n\n';
        break;
      case 'certifications':
        for (const item of section.items || []) {
          if (item) text += `- ${item}\n`;
        }
        text += '\n';
        break;
      case 'projects':
        for (const item of section.items || []) {
          text += `${item.name}${item.tech ? ` (${item.tech})` : ''}\n`;
          if (item.description) text += `${item.description}\n`;
          if (item.url) text += `${item.url}\n`;
          text += '\n';
        }
        break;
      default:
        text += (section.content || '') + '\n\n';
    }
  }
  return text.trim();
};
